import { useJoinCircleMutation } from "@/api/circles-api";
import handsImage from "@/assets/images/hands.jpeg";
import LogoSymbol from "@/assets/images/logo-symbol.svg?react";
import useUserStore from "@/store/userStore";
import { Circle } from "@/types/circle";
import { getLoginURL } from "@/utils/auth";
import { useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Show from "../Show";
import { Button } from "../ui/button";

interface EFBCircleProps {
  circle: Circle;
}
export default function EFBCircle({ circle }: Readonly<EFBCircleProps>) {
  const { t } = useTranslation();
  const { mutate, isPending } = useJoinCircleMutation();
  const { token } = useUserStore();
  const router = useRouter();

  function handleJoin() {
    if (!token) {
      window.location.href = getLoginURL("");
    } else {
      mutate(
        { id: circle.id },
        {
          onSuccess: () => {
            toast.success(t("joinCircleModal.success"));
            router.navigate({ to: "/circle/" + circle.id });
          },
          onError: () => {
            toast.error(t("joinCircleModal.failed"));
          },
        },
      );
    }
  }

  return (
    <section
      className="relative aspect-square min-h-[360px] overflow-hidden rounded-2xl !bg-cover !bg-center p-4 text-white sm:aspect-auto md:px-6 md:py-14"
      style={{
        background: `linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), url(${handsImage}) lightgray -186.167px 0.26px / 158.436% 100.818% no-repeat`,
      }}
    >
      <LogoSymbol className="absolute -bottom-3 -end-3 h-auto max-w-[33%] md:bottom-0 md:end-6" />
      <div className="relative">
        <h3 className="font-size-h2 mb-4 max-w-[600px]">{t("efbCircle.title")}</h3>
        <p className="font-size-p mb-6 max-w-[600px] md:mb-8">{t("efbCircle.description")}</p>
        <Button
          size="lg"
          className="w-full text-base md:w-auto"
          onClick={handleJoin}
          disabled={isPending || circle.is_user_joined}
        >
          <Show when={isPending}>
            <Loader2 className="size-4 animate-spin" />
          </Show>
          <Show when={!circle.is_user_joined} fallback={t("joinCircleModal.alreadyJoined")}>
            {t("efbCircle.button")}
          </Show>
        </Button>
      </div>
    </section>
  );
}
